import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { supabase } from '../lib/supabase'
import Icon from './ui/Icon'
import './Auth.css'

export default function Login({ onNavigate }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true)
    setMessage('')
    setIsError(false)

    console.log('🔐 Login attempt started for:', email)

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      console.log('📡 Supabase response:', { data, error })

      if (error) {
        console.error('❌ Supabase error:', error.message)
        setMessage(error.message)
        setIsError(true)
      } else {
        console.log('✅ Login successful:', data.user?.email)
        // Auth state change will handle the redirect
      }
    } catch (error) {
      console.error('❌ Catch block error:', error)
      setMessage(`Network error: ${error.message}`)
      setIsError(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-container">
      {/* Floating Clouds */}
      <div className="auth-clouds">
        <img src="/assets/cloud1.png" alt="" className="auth-cloud auth-cloud-1" />
        <img src="/assets/cloud1.png" alt="" className="auth-cloud auth-cloud-2" />
        <img src="/assets/cloud1.png" alt="" className="auth-cloud auth-cloud-3" />
        <img src="/assets/cloud1.png" alt="" className="auth-cloud auth-cloud-4" />
      </div>

      {/* Sparkle Decorations */}
      <div className="auth-sparkles">
        <div className="auth-sparkle auth-sparkle-1">
          <Icon name="sparkle" size="sm" color="#FFD700" />
        </div>
        <div className="auth-sparkle auth-sparkle-2">
          <Icon name="sparkle" size="xs" color="#DDA0DD" />
        </div>
        <div className="auth-sparkle auth-sparkle-3">
          <Icon name="star" size="xs" color="#87CEEB" />
        </div>
        <div className="auth-sparkle auth-sparkle-4">
          <Icon name="sparkle" size="sm" color="#FFB6C1" />
        </div>
        <div className="auth-sparkle auth-sparkle-5">
          <Icon name="star" size="xs" color="#98D8C8" />
        </div>
        <div className="auth-sparkle auth-sparkle-6">
          <Icon name="sparkle" size="xs" color="#E6E6FA" />
        </div>
      </div>

      <motion.div 
        className="auth-content"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        {/* Logo/Title Section */}
        <motion.div 
          className="auth-header"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <h1 className="auth-title">Rainbow Bridge</h1>
          <p className="auth-subtitle">
            Keep your beloved pets close to your heart
          </p>
        </motion.div>

        {/* Login Form Card */}
        <motion.div 
          className="auth-card"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h2 className="auth-card-title">Welcome Back</h2>
          <p className="auth-card-description">
            Sign in to continue your journey
          </p>
          
          <form onSubmit={handleLogin} className="auth-form">
            <div className="auth-input-group">
              <label htmlFor="email" className="auth-label">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="auth-input"
                required
                disabled={loading}
              />
            </div>

            <div className="auth-input-group">
              <label htmlFor="password" className="auth-label">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="auth-input"
                required
                disabled={loading}
              />
            </div>

            {/* Forgot Password Link */}
            <div style={{ textAlign: 'right', marginTop: '-0.5rem' }}>
              <button
                type="button"
                onClick={() => onNavigate?.('forgot-password')}
                className="auth-link"
              >
                Forgot password?
              </button>
            </div>

            <motion.button
              type="submit"
              disabled={loading || !email || !password}
              className="auth-submit-btn"
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              transition={{ duration: 0.2 }}
            >
              {loading ? (
                <span className="auth-spinner">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </motion.button>
          </form>

          {/* Error Message Display */}
          {message && isError && (
            <motion.div 
              className="auth-message auth-message-error"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <p>{message}</p>
            </motion.div>
          )}

          {/* Sign Up Link */}
          <div className="auth-nav">
            <p className="auth-nav-text">
              Don't have an account?
              <button
                type="button"
                onClick={() => onNavigate?.('signup')}
                className="auth-nav-link"
              >
                Sign up
              </button>
            </p>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div 
          className="auth-footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <p className="auth-footer-text">
            Made with <Icon name="heart" size="xs" color="#FFB6C1" /> for pet lovers everywhere
          </p>
        </motion.div>
      </motion.div>
    </div>
  )
}
